import {noop} from '@benchling/js-utils';
import React from 'react';

import {PartJSON} from 'biojs/Part';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import * as BioentityActions from 'inventory/BioentityActions';
import {HoistedTag} from 'inventory/InventoryFileConstants';
import MetadataTableContent, {MetadataTableColumnDefinition} from 'metadata/MetadataTableContent';
import MetadataTableHeader from 'metadata/MetadataTableHeader';
import {SchemaField, SchemaFieldPath} from 'schemas/fields/SchemaFieldConstants';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import {Sublabel} from 'ui-library/Headers';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import {ElementKeyboardEvent} from 'util/Types';

import HoistedTagRows from './hoisted-tags/HoistedTagRows';
import TagNameCell from './TagNameCell';
import TagValueCell from './TagValueCell';

export interface BaseTagTableProps {
  canAddOrRemoveTags: boolean;
  canRefreshTags: boolean;
  isLoadingTags: boolean;
  isNameEditable: boolean;
  emptyMessage: string;
  errorsByFieldId: {[fieldId: number]: Array<SchemaValidationErrorRecord>};
  fieldsForCustomTagNameValidation: Array<SchemaField>;
  fileId?: string;
  itemLinkAllowed: boolean;
  hoistedTags: Array<HoistedTag>;
  hoistedTagSchemaGraphs: Array<SchemaFieldPath>;
  sequenceParts?: Array<PartJSON> | null;
  subheaderNode?: React.ReactNode;
  title: string | React.ReactNode;
  renderAutofillButton?: (isEditing: boolean) => JSX.Element | null;
  additionalHeaderIcons?: React.ReactNode;
}
interface Props extends BaseTagTableProps {
  // FieldValues
  tags: Array<Tag | null>;
  // Editing state
  areTagsEditable: boolean;
  isEditing: boolean;
  disableEditWithTooltip?: string | null;
  // Event handlers
  onUpdateTag?: (tagIndex: number, newValue: Tag) => void;
  onRemoveTag?: (tagIndex: number) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onEdit?: () => void;
  onCreateTag?: () => void;
  onCancelUpdate?: () => void;
  onCompleteUpdate?: () => void;
}

type CatchAllTag = Tag | HoistedTag | null;

class TagTable extends React.Component<Props> {
  static defaultProps = {
    canAddOrRemoveTags: false,
    isEditing: false,
    canRefreshTags: false,
    errorsByFieldId: {},
    itemLinkAllowed: true,
    fieldsForCustomTagNameValidation: [],
    hoistedTags: [],
    hoistedTagSchemaGraphs: [],
    areTagsEditable: false,
    isLoadingTags: false,
    isNameEditable: false,
    sequenceParts: [],
  };

  /**
   * Return all writable tags, tags staged for deletion,
   * and read-only hoisted tags (`props.hoistedTags`).
   */
  private getAllTags = (): Array<CatchAllTag> => this.props.tags.concat(this.props.hoistedTags);

  private handleRefetchTags = (): void => {
    // This is only applicable to files. The `canRefreshTags` prop determines whether to show the
    // refresh button, which is currently only shown for file tags on the metadata tab.
    BioentityActions.getTags(this.props.fileId!);
  };

  private getFieldColumnHeader = (): JSX.Element => (
    <div className="metadata-header-cell simpleDivTable-headerCell">
      <Sublabel className="metadata-table-subheader">
        Field
        {this.props.canAddOrRemoveTags && <AddIcon onClick={this.props.onCreateTag} />}
      </Sublabel>
    </div>
  );

  private getValueColumnHeader = (): JSX.Element => (
    <div className="metadata-header-cell simpleDivTable-headerCell">
      <div className="u-lr-split u-vertical-center-items">
        <Sublabel className="metadata-table-subheader">Value</Sublabel>
        <span className="metadata-header-icons">
          {this.props.additionalHeaderIcons}
          {this.props.canRefreshTags && !this.props.isEditing && (
            <Tooltip title={this.props.isLoadingTags ? 'Refreshing fields...' : 'Refresh fields'}>
              <div>
                {this.props.isLoadingTags ? (
                  <Spinner light={true} />
                ) : (
                  <BenchlingIcon
                    className="u-pointer u-mini-spacing-left"
                    icon="refresh"
                    onClick={this.handleRefetchTags}
                  />
                )}
              </div>
            </Tooltip>
          )}
        </span>
      </div>
    </div>
  );

  private getMetadataColumnDefinitions = (): Array<MetadataTableColumnDefinition<Tag>> => [
    {
      columnHeader: this.getFieldColumnHeader(),
      // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
      mapItemToColumnContent: (tag: Tag, rowIndex: number) => (
        <TagNameCell
          isEditing={this.props.isEditing}
          isNameEditable={this.props.isNameEditable}
          onKeyDown={this.props.onKeyDown ? this.props.onKeyDown : noop}
          onUpdateTag={this.props.onUpdateTag ? this.props.onUpdateTag : noop}
          sequenceParts={this.props.sequenceParts}
          shouldAutoFocus={rowIndex === 0 || !tag.name}
          tagIndex={rowIndex}
          tag={tag}
        />
      ),
    },
    {
      columnHeader: this.getValueColumnHeader(),
      // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
      mapItemToColumnContent: (tag: SchemaTag, rowIndex: number) => (
        <TagValueCell
          errors={tag.tagSchemaField ? this.props.errorsByFieldId[tag.tagSchemaField.id] : null}
          fileId={this.props.fileId}
          itemLinkAllowed={this.props.itemLinkAllowed}
          isEditing={this.props.isEditing}
          onKeyDown={this.props.onKeyDown ? this.props.onKeyDown : noop}
          onUpdateTag={this.props.onUpdateTag ? this.props.onUpdateTag : noop}
          sequenceParts={this.props.sequenceParts}
          schemaFieldSelectorId={tag.tagSchemaField?.schemaFieldSelectorId ?? undefined}
          shouldAutoFocus={false}
          tagIndex={rowIndex}
          tag={tag}
        />
      ),
    },
  ];

  override render(): JSX.Element {
    return (
      <div
        className="metadata-table simpleDivTable"
        {...getTestProps({component: 'TagTable', element: 'root'})}
      >
        <MetadataTableHeader
          isEditable={this.props.areTagsEditable && this.props.tags.length > 0}
          isEditing={this.props.isEditing}
          title={this.props.title}
          headerContent={this.props.subheaderNode}
          additionalButtons={this.props.renderAutofillButton?.(this.props.isEditing)}
          onEdit={this.props.onEdit}
          handleCancelEdit={this.props.onCancelUpdate}
          handleCompleteEdit={this.props.onCompleteUpdate}
          disableEditWithTooltip={this.props.disableEditWithTooltip}
        />
        <MetadataTableContent
          items={this.getAllTags()}
          isEmpty={this.getAllTags().length === 0}
          columnDefinitions={this.getMetadataColumnDefinitions()}
          emptyMessage={this.props.emptyMessage}
          isEditing={this.props.isEditing}
          isLoading={false}
          canRemoveRows={this.props.canAddOrRemoveTags}
          handleRemoveRow={this.props.onRemoveTag ? this.props.onRemoveTag : noop}
          confirmDeleteModalBody={
            <>Are you sure you want to delete this custom field? This action is irreversible.</>
          }
        />
        <HoistedTagRows tags={this.props.hoistedTags} tagSchemaGraphs={this.props.hoistedTagSchemaGraphs} />
      </div>
    );
  }
}

export default TagTable;
